/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <teleport to="body">
    <transition name="fade">
   <div

       v-if="isVisible"
       class="draggable-modal shadow-lg"
       :style="modalStyle"
       ref="modalRef"
   >
        <div
            class="draggable-header"
            @mousedown="startDrag"
        >
          <div class="d-flex align-items-center gap-2">
            <i v-if="icon" :class="icon" class="text-primary"></i>
            <span class="fw-bold text-dark user-select-none">{{ title }}</span>
          </div>

          <button class="btn btn-sm btn-link text-secondary p-0 close-btn" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="draggable-body custom-scrollbar">
          <slot></slot>
        </div>

      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: "DraggableModal",
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: "Okienko"
    },
    icon: {
      type: String,
      default: null // np. 'fas fa-info-circle'
    },
    initialX: {
      type: Number,
      default: 100
    },
    initialY: {
      type: Number,
      default: 100
    },
    width: {
      type: String,
      default: "400px"
    },
    height: {
      type: String,
      default: "auto"
    }
  },
  emits: ["update:modelValue", "position-change", "close"],
  data() {
    return {
      x: 0,
      y: 0,
      isDragging: false,
      dragOffset: { x: 0, y: 0 }

    };
  },
  computed: {
    isVisible: {
      get() { return this.modelValue; },
      set(val) { this.$emit("update:modelValue", val); }
    },
    modalStyle() {
      return {
        left: `${this.x}px`,
        top: `${this.y}px`,
        width: this.width,
        height: this.height
      };
    }
  },
  watch: {
    // Jeśli modal zostanie otwarty ponownie, a propsy się zmieniły - zaktualizuj pozycję
    modelValue(val) {
      if (val) {
        // Opcjonalnie: Resetuj pozycję przy każdym otwarciu, jeśli taka wola
        // this.x = this.initialX;
        // this.y = this.initialY;
      }
    }
  },
  mounted() {
    this.x = this.initialX;
    this.y = this.initialY;
  },
  methods: {
    close() {
      this.isVisible = false;
      this.$emit("close");
    },

    // --- DRAG LOGIC ---

    startDrag(event) {
      // Zapobiegamy zaznaczaniu tekstu podczas dragowania
      // event.preventDefault();

      this.isDragging = true;

      // Obliczamy, w którym miejscu nagłówka kliknął użytkownik względem lewego górnego rogu okna
      // clientX - aktualna pozycja okna (x) = offset
      this.dragOffset.x = event.clientX - this.x;
      this.dragOffset.y = event.clientY - this.y;

      // Dodajemy listenery do window, aby drag działał płynnie nawet jak myszka wyjedzie poza okno
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },

    onDrag(event) {
      if (!this.isDragging) return;

      // Oblicz nową pozycję
      let newX = event.clientX - this.dragOffset.x;
      let newY = event.clientY - this.dragOffset.y;

      // (Opcjonalnie) Ograniczenia ekranu - żeby nie wyciągnąć okna poza viewport
      // const maxX = window.innerWidth - this.$refs.modalRef.offsetWidth;
      // const maxY = window.innerHeight - this.$refs.modalRef.offsetHeight;
      // if (newX < 0) newX = 0;
      // if (newY < 0) newY = 0;
      // if (newX > maxX) newX = maxX;
      // if (newY > maxY) newY = maxY;

      // Tylko proste zabezpieczenie przed ucieczką w górę poza ekran
      if (newY < 0) newY = 0;

      this.x = newX;
      this.y = newY;

      // Emituj aktualną pozycję
      this.$emit("position-change", { x: this.x, y: this.y });
    },

    stopDrag() {
      this.isDragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    }
  }
};
</script>

<style scoped>
.draggable-modal {
  position: fixed; /* Kluczowe dla swobodnego przesuwania */
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #c8c6c4;
  display: flex;
  flex-direction: column;
  z-index: 1060; /* Wyższy niż standardowe modale Bootstrapa (1050) */
  font-family: "Segoe UI", sans-serif;
  overflow: hidden;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.draggable-header {
  padding: 10px 14px;
  background-color: #f3f2f1;
  border-bottom: 1px solid #e1dfdd;
  cursor: move; /* Kursor sugerujący przesuwanie */
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none; /* Zapobiega zaznaczaniu tekstu nagłówka */
}

.draggable-header:active {
  background-color: #e1dfdd; /* Lekkie przyciemnienie przy kliknięciu */
  cursor: grabbing;
}

.draggable-body {
  padding: 16px;
  flex-grow: 1;
  overflow-y: auto;
  max-height: 80vh; /* Zabezpieczenie na wypadek dużej treści */
  background-color: #fff;
}

.close-btn {
  font-size: 1.1rem;
  transition: color 0.2s;
}
.close-btn:hover {
  color: #a80000 !important;
}

/* Animacja Fade (opcjonalnie) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>